import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];

export default function ContractorJMRs() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [jmrs, setJmrs] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [jmrRes, woRes] = await Promise.all([api.get("/procurement/contractor-jmrs"), api.get("/procurement/work-orders")]);
      setJmrs(jmrRes.data);
      setWorkOrders(woRes.data.filter((w) => w.status === "APPROVED"));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const lineLabel = (woItemId) => {
    for (const wo of workOrders) {
      const item = wo.items.find((i) => i.id === woItemId);
      if (item) return `${wo.number} — ${item.description}`;
    }
    return woItemId;
  };

  const certify = async (jmr) => {
    setBusyId(jmr.id);
    try {
      await api.post(`/procurement/contractor-jmrs/${jmr.id}/certify`);
      toast.success("JMR certified");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="contractor-jmrs-page">
      <PageHeader title="Contractor JMRs" description="Joint measurement of work done against a work order line, capped at 10% over the ordered qty." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateJMRDialog workOrders={workOrders} onCreated={load} />}</div>} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && jmrs.length === 0 ? (
          <EmptyState title="No JMRs yet" testId="jmrs-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Work order line</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Measured qty</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(jmrs, (r) => r.project_id).map((jmr) => (
                <TableRow key={jmr.id} data-testid={`jmr-row-${jmr.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{jmr.number}</TableCell>
                  <TableCell className="text-foreground">{lineLabel(jmr.work_order_item_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{jmr.measurement_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{jmr.measured_qty}</TableCell>
                  <TableCell><StatusPill value={jmr.status} /></TableCell>
                  <TableCell className="text-right">
                    {canWrite && jmr.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === jmr.id} onClick={() => certify(jmr)} data-testid={`certify-jmr-button-${jmr.number}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Certify
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateJMRDialog({ workOrders, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ work_order_item_id: "", measurement_date: "", measured_qty: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/contractor-jmrs", form);
      toast.success("JMR recorded");
      setForm({ work_order_item_id: "", measurement_date: "", measured_qty: "", remarks: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-jmr-button"><Plus className="h-4 w-4" /> New JMR</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Contractor JMR</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-jmr-form">
          <div>
            <Label>Work order line</Label>
            <Select required value={form.work_order_item_id} onValueChange={(v) => setForm({ ...form, work_order_item_id: v })}>
              <SelectTrigger data-testid="jmr-wo-item-select" className="mt-1.5"><SelectValue placeholder="Select an approved WO line" /></SelectTrigger>
              <SelectContent>
                {workOrders.flatMap((wo) => wo.items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>{wo.number} — {item.description}</SelectItem>
                )))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="jmr-date-input" value={form.measurement_date} onChange={set("measurement_date")} className="mt-1.5" /></div>
            <div><Label>Measured qty</Label><Input required type="number" step="0.001" data-testid="jmr-qty-input" value={form.measured_qty} onChange={set("measured_qty")} className="mt-1.5" /></div>
          </div>
          <div><Label>Remarks</Label><Input data-testid="jmr-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-jmr-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
